import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NavigationItem } from '../../types';
import logo from '../../assets/images/logo.png';

const ORDER_LINK = 'https://grill-me-crazy-indo-mexican-alice-springs.nextorder.com/';

const navigationItems: NavigationItem[] = [
  { name: 'HOME', href: '/' },
  { name: 'OUR STORY', href: '/#our-story' },
  { name: 'OFFERS', href: '/#offers' },
  { name: 'MEET EL JEFE', href: '/#el-jefe' },
  { name: 'MENU', href: '/menu' },
  { name: 'GALLERY', href: '/gallery' },
];

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const closeMenu = () => setIsOpen(false);

  const handleNavClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    closeMenu();
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      if (location.pathname !== '/') {
        navigate('/');
        const waitForElement = (attempts = 0) => {
          const el = document.getElementById(sectionId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          } else if (attempts < 20) {
            setTimeout(() => waitForElement(attempts + 1), 100);
          }
        };
        setTimeout(() => waitForElement(), 300);
      } else {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (href === '/') {
      if (location.pathname !== '/') {
        navigate('/');
        window.scrollTo(0, 0);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  const isActive = (href: string) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && location.hash === href.replace('/', '');
    }
    return location.pathname === href;
  };

  const mobileMenu = isOpen ? createPortal(
    <div
      id="mobile-menu-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#EC461C',
        zIndex: 2147483647,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
      }}
    >
      {/* Close + Logo */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
        <button onClick={closeMenu} aria-label="Close menu" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: '28px', height: '28px', display: 'block' }}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <img src={logo} alt="Grill Me Crazy Logo" style={{ height: '52px', width: 'auto' }} />
        <div style={{ width: '44px' }}></div>
      </div>

      {/* Nav Items + Buttons - scrollable */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '20px', padding: '20px 32px 40px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {navigationItems.map((item) => (
          <a
            key={item.name}
            href={item.href}
            onClick={(e) => handleNavClick(item.href, e)}
            className="font-teko"
            style={{
              color: isActive(item.href) ? '#FFC600' : '#FFFFFF',
              fontSize: '36px',
              fontWeight: 500,
              letterSpacing: '2px',
              textDecoration: 'none',
              textTransform: 'uppercase',
              textAlign: 'center',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              width: '100%',
              padding: '6px 0',
              flexShrink: 0,
            }}
          >
            {item.name}
          </a>
        ))}

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '320px', marginTop: '16px', flexShrink: 0 }}>
          <a href={ORDER_LINK} target="_blank" rel="noopener noreferrer"
            style={{
              backgroundColor: '#FFC600',
              color: '#000000',
              fontSize: '22px',
              fontWeight: 600,
              fontFamily: "'Teko', sans-serif",
              letterSpacing: '1px',
              textAlign: 'center',
              padding: '14px 20px',
              border: '1px solid #151515',
              borderRadius: '8px',
              boxShadow: '0 4px 0 0 #151515',
              textDecoration: 'none',
              display: 'block',
              flexShrink: 0,
            }}>
            ORDER ONLINE
          </a>
          <a href={ORDER_LINK} target="_blank" rel="noopener noreferrer"
            style={{
              backgroundColor: '#027C3F',
              color: '#FFFFFF',
              fontSize: '22px',
              fontWeight: 600,
              fontFamily: "'Teko', sans-serif",
              letterSpacing: '1px',
              textAlign: 'center',
              padding: '14px 20px',
              border: '1px solid #151515',
              borderRadius: '8px',
              boxShadow: '0 4px 0 0 #151515',
              textDecoration: 'none',
              display: 'block',
              flexShrink: 0,
            }}>
            RESERVATION
          </a>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* ========== NAVBAR BAR ========== */}
      <nav
        className="fixed top-0 left-0 w-full z-[100]"
        style={{ backgroundColor: '#EC461C' }}
      >
        <div className="flex items-center justify-between h-[70px] md:h-[85px] lg:h-[100px] px-4 md:px-6 lg:px-8">
          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={(e) => handleNavClick(item.href, e)}
                className={`font-teko font-normal transition-colors relative group ${
                  isActive(item.href) ? 'text-white' : 'text-white hover:text-yellow-200'
                }`}
                style={{ fontSize: '27px' }}
              >
                {item.name}
                <span className={`absolute bottom-0 left-0 h-0.5 bg-white transition-all duration-200 ${
                  isActive(item.href) ? 'w-full' : 'w-0 group-hover:w-full'
                }`}></span>
              </Link>
            ))}
          </div>

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2" onClick={(e) => handleNavClick('/', e)}>
            <img src={logo} alt="Grill Me Crazy Logo" className="h-10 md:h-12 lg:h-16 w-auto" />
          </Link>

          {/* Desktop Buttons */}
          <div className="hidden lg:flex items-center space-x-3 xl:space-x-4">
            <div style={{ boxShadow: '0 4px 0 0 #151515', borderRadius: '8px' }}>
              <a href={ORDER_LINK} target="_blank" rel="noopener noreferrer"
                className="font-teko font-normal text-black block transition-all duration-200 hover:translate-y-[4px] hover:bg-yellow-400"
                style={{ backgroundColor: '#FFC600', padding: '15px 20px', fontSize: '18px', height: '47px', display: 'flex', alignItems: 'center', border: '1px solid #151515', cursor: 'pointer', borderRadius: '8px' }}>
                ORDER ONLINE
              </a>
            </div>
            <div style={{ boxShadow: '0 4px 0 0 #151515', borderRadius: '8px' }}>
              <a href={ORDER_LINK} target="_blank" rel="noopener noreferrer"
                className="font-teko font-normal text-white block transition-all duration-200 hover:translate-y-[4px] hover:bg-green-600"
                style={{ backgroundColor: '#027C3F', padding: '15px 20px', fontSize: '18px', height: '47px', display: 'flex', alignItems: 'center', border: '1px solid #151515', cursor: 'pointer', borderRadius: '8px' }}>
                RESERVATION
              </a>
            </div>
          </div>

          {/* Hamburger */}
          <button className="lg:hidden flex flex-col gap-[5px] p-2" onClick={() => setIsOpen(true)} aria-label="Open menu">
            <span className="w-6 h-[3px] bg-white rounded-full"></span>
            <span className="w-6 h-[3px] bg-white rounded-full"></span>
            <span className="w-6 h-[3px] bg-white rounded-full"></span>
          </button>
        </div>
      </nav>

      {/* ========== FULL SCREEN MOBILE MENU - PORTAL ========== */}
      {mobileMenu}
    </>
  );
};

export default Navbar;
